/*
 * Wild Palace — GA4 custom-event publisher (Shopify Customer Events)
 * -----------------------------------------------------------------------------
 * Single source of truth for GA4 is the Customer Events web pixel
 * (analytics/ga4-customer-events-pixel.js, installed in Shopify admin → Customer
 * events). That pixel already receives Shopify's STANDARD events and forwards
 * them to GA4: page_view, view_item, view_item_list, add_to_cart, search,
 * begin_checkout (incl. wallet / Shop Pay), add_shipping_info, add_payment_info,
 * and purchase. The theme must NOT send those again — that was the old GTM
 * dataLayer path and it double-counted against Shopify's native integration.
 *
 * This file only PUBLISHES the events Shopify has no standard event for, using
 * Shopify.analytics.publish(). The pixel subscribes to them and forwards to GA4:
 *   - wild_add_sample / wild_sample_request   (PDP "Order a sample" click)
 *   - wild_add_to_wishlist / wild_view_wishlist (WishlistHero hooks)
 *   - wild_view_on_wall        (visualizer "See it on your wall" — WP-A1)
 *   - wild_customize_start      (PDP "Show price and customize" gate — WP-A2)
 *   - wild_quick_view / wild_view_similar (resource-card quick-view — WP-A3)
 *   - wild_select_item          (product-tile click w/ list attribution — WP-A5)
 *   - wild_view_cart            (cart page load / cart drawer open — WP-A6)
 *   - wild_remove_from_cart     (cart "Remove" click — completes the cart funnel)
 *   - wild_form_start / wild_form_submit        (named contact + newsletter forms)
 *
 * Custom-event payloads are already GA4-shaped; the pixel passes them straight
 * to gtag. Requires the Customer Events pixel to be installed and connected.
 */
(function () {
  'use strict';

  // Publish a custom event to the Customer Events pixel. No-ops safely if the
  // storefront analytics SDK or the pixel is not present.
  function publish(name, payload) {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(name, payload || {});
      }
    } catch (e) {
      /* never let analytics break the page */
    }
  }
  window.wpGA4 = { publish: publish };

  function readJSON(el) {
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }

  // ---------------------------------------------------------------------------
  // add_sample / sample_request (PDP "Order a sample")
  // ---------------------------------------------------------------------------
  function parsePrice(text) {
    if (!text) return undefined;
    var n = parseFloat(String(text).replace(/[^0-9.,]/g, '').replace(/,(?=\d{3}\b)/g, '').replace(',', '.'));
    return isNaN(n) ? undefined : n;
  }

  function initSample() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.buy_sample_action');
      if (!btn) return;

      var ctx = readJSON(document.querySelector('script[data-ga4-product]')) || {};
      var priceEl = document.getElementById('buy_sample_price');
      var currencyEl = document.getElementById('buy_sample_currency');
      var value = parsePrice(priceEl && priceEl.textContent);
      var currency = ctx.currency || (currencyEl && currencyEl.textContent.trim());

      var baseItem = ctx.item || {};
      var item = {
        item_id: baseItem.item_id,
        item_name: baseItem.item_name,
        item_brand: baseItem.item_brand || 'Wild Palace',
        item_category: 'Sample',
        item_variant: 'Sample',
        price: value,
        quantity: 1
      };

      var ecommerce = { currency: currency, value: value, items: [item] };
      publish('wild_add_sample', ecommerce);
      publish('wild_sample_request', ecommerce);
    });
  }

  // ---------------------------------------------------------------------------
  // add_to_wishlist / view_wishlist (WishlistHero)
  // ---------------------------------------------------------------------------
  // WishlistHero hydrates its heart controls at runtime, so we delegate on the
  // document and resolve the product from the nearest [data-product-id] (cards)
  // or the PDP product JSON. The app toggles add/remove on one control, so we
  // read the control's state just after the click and only publish on "add".
  // NOTE: the exact "added" class is app-rendered; validate the selectors below
  // against a live preview and adjust ADDED_HINT if needed.
  // Prefer the theme's OWN explicit wishlist hooks — data-rc-wishlist (product
  // card overlay heart) and data-wild-wishlist (PDP button) — over guessing
  // WishlistHero's vendor classes; keep the vendor selectors as a fallback.
  // NOTE: the add-vs-remove "added" state (ADDED_HINT / looksAdded) is still read
  // from the app-rendered control and should be validated on a live dev preview.
  var WL_CONTROL = '[data-rc-wishlist], [data-wild-wishlist], [class*="wishlisthero"], .wishlisthero-floating, .wishlisthero-pdp, [data-wishlisthero]';
  var ADDED_HINT = /added|active|selected|in-?wishlist|is-?added|filled/i;

  function wishlistItem(el) {
    var card = el.closest('[data-product-id]');
    if (card) {
      var titleEl = card.querySelector('.resource-card__title, .product-card__title, [data-product-title]');
      return {
        item_id: String(card.getAttribute('data-product-id')),
        item_name: titleEl ? titleEl.textContent.trim() : undefined,
        item_brand: 'Wild Palace'
      };
    }
    var ctx = readJSON(document.querySelector('script[data-ga4-product]')) || {};
    return ctx.item || null;
  }

  function looksAdded(el) {
    try {
      var ctrl = el.closest(WL_CONTROL) || el;
      if (ctrl.getAttribute && ctrl.getAttribute('aria-pressed') === 'true') return true;
      if (ctrl.className && ADDED_HINT.test(ctrl.className)) return true;
      // also check a hydrated child icon
      var inner = ctrl.querySelector && ctrl.querySelector('[class*="added"], [aria-pressed="true"]');
      return !!inner;
    } catch (e) { return false; }
  }

  function initWishlist() {
    document.addEventListener('click', function (e) {
      // add / remove toggle
      var ctrl = e.target.closest(WL_CONTROL);
      if (ctrl) {
        var item = wishlistItem(ctrl);
        if (item && item.item_id) {
          // let the app update its own state, then publish only if now "added"
          setTimeout(function () {
            if (looksAdded(ctrl)) publish('wild_add_to_wishlist', { items: [item] });
          }, 120);
        }
        return;
      }
      // open wishlist (header icon / drawer anchor)
      var open = e.target.closest('.wishlist-hero-header-icon, [href="#hero-wishlist"], [data-wishlisthero-open]');
      if (open) publish('wild_view_wishlist', {});
    });
  }

  // ---------------------------------------------------------------------------
  // form_start / form_submit (named lead forms only)
  // ---------------------------------------------------------------------------
  var FORM_NAMES = {
    WildContactForm: 'Contact / custom sizing enquiry',
    WildNewsletterForm: 'Newsletter signup',
    WildFooterSignupForm: 'Footer email signup',
    WpPromoForm: 'Newsletter signup' // welcome-offer promo drawer (maps to sign_up)
  };

  // Utility / commerce forms we do NOT count as lead forms.
  var FORM_EXCLUDE = /localization|predictive-search|facets|cart-discount|\bcart[-_]form\b|product-form|product_form|address_form/i;

  function formName(form) {
    var id = form.id || '';
    var cls = form.className || '';
    if (form.getAttribute('data-ga4-form-name')) return form.getAttribute('data-ga4-form-name');
    if (FORM_NAMES[id]) return FORM_NAMES[id];
    if (/klaviyo/i.test(cls)) return 'Klaviyo signup';
    if (/email-signup|newsletter/i.test(cls)) return 'Newsletter signup';
    if (id) return id;
    return form.getAttribute('action') || 'form';
  }

  function formMeta(form) {
    return { form_id: form.id || '', form_name: formName(form), form_destination: form.action || '' };
  }

  function shouldTrack(form) {
    if (form.dataset.ga4Bound) return false;
    if (form.classList.contains('ga4-form') || form.hasAttribute('data-ga4-form-name')) return true;
    var key = (form.id || '') + ' ' + (form.className || '') + ' ' + (form.getAttribute('action') || '');
    return !FORM_EXCLUDE.test(key);
  }

  function bindForm(form) {
    if (!shouldTrack(form)) return;
    form.dataset.ga4Bound = '1';
    form.addEventListener('focusin', function once() {
      publish('wild_form_start', formMeta(form));
      form.removeEventListener('focusin', once);
    });
    form.addEventListener('submit', function () {
      publish('wild_form_submit', formMeta(form));
    });
  }

  function initForms() {
    document.querySelectorAll('form').forEach(bindForm);
  }

  // Klaviyo (and other embeds) inject their <form> after load — watch for them.
  function watchForms() {
    if (!window.MutationObserver) return;
    var mo = new MutationObserver(function (mutations) {
      mutations.forEach(function (m) {
        for (var i = 0; i < m.addedNodes.length; i++) {
          var n = m.addedNodes[i];
          if (n.nodeType !== 1) continue;
          if (n.tagName === 'FORM') bindForm(n);
          else if (n.querySelectorAll) n.querySelectorAll('form').forEach(bindForm);
        }
      });
    });
    mo.observe(document.body, { childList: true, subtree: true });
  }

  function productCtx() {
    return readJSON(document.querySelector('script[data-ga4-product]')) || {};
  }

  // ---------------------------------------------------------------------------
  // view_on_wall — visualizer "See it on your wall" (WP-A1). Highest-value VTA
  // signal. Capture phase so it runs before the visualizer's own scroll-restore
  // / navigation handlers. Does not block the visualizer opening.
  // ---------------------------------------------------------------------------
  function initVisualizer() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.wp-gallery-wallbtn, [data-wp-visualizer], a[href*="mockup.wildpalace.com"]');
      if (!btn) return;
      var ctx = productCtx();
      publish('wild_view_on_wall', { items: ctx.item ? [ctx.item] : [], currency: ctx.currency });
    }, true);
  }

  // ---------------------------------------------------------------------------
  // customize_start — the PDP price-reveal gate "Show price and customize" (WP-A2)
  // ---------------------------------------------------------------------------
  function initCustomizer() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.cropper_cst_button, .customize-button');
      if (!btn) return;
      var ctx = productCtx();
      publish('wild_customize_start', { items: ctx.item ? [ctx.item] : [], currency: ctx.currency });
    });
  }

  // ---------------------------------------------------------------------------
  // quick_view / view_similar — resource-card actions (WP-A3). The "Find similar"
  // variant carries data-rc-related; send view_similar for it and quick_view for
  // the plain quick-view, so neither double-fires.
  // ---------------------------------------------------------------------------
  function initQuickView() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-rc-quickview]');
      if (!btn) return;
      var payload = {
        item_id: btn.dataset.productId ? String(btn.dataset.productId) : undefined,
        item_type: btn.dataset.productType || undefined
      };
      if (btn.hasAttribute('data-rc-related')) publish('wild_view_similar', payload);
      else publish('wild_quick_view', payload);
    });
  }

  // ---------------------------------------------------------------------------
  // select_item — product-tile click with list attribution (WP-A5). The list
  // context (item_list_id / item_list_name + items[]) is emitted as a
  // [data-ga4-items] JSON block near each grid; resolve the clicked card by its
  // product id.
  // ---------------------------------------------------------------------------
  function initSelectItem() {
    document.addEventListener('click', function (e) {
      var link = e.target.closest('.resource-card a[href]');
      if (!link) return;
      var card = link.closest('[data-product-id]');
      if (!card) return;
      var productId = String(card.getAttribute('data-product-id'));
      var scope = card.closest('.shopify-section') || document;
      var list = readJSON(scope.querySelector('script[data-ga4-items]'));
      if (!list && scope !== document) list = readJSON(document.querySelector('script[data-ga4-items]'));
      if (!list || !list.items) return;
      var item = null;
      for (var i = 0; i < list.items.length; i++) {
        if (String(list.items[i].item_id) === productId) { item = list.items[i]; break; }
      }
      if (!item) return;
      publish('wild_select_item', {
        item_list_id: list.item_list_id || undefined,
        item_list_name: list.item_list_name || undefined,
        items: [item]
      });
    });
  }

  // ---------------------------------------------------------------------------
  // view_cart — consume the [data-ga4-cart] context (WP-A6). Fires on the cart
  // page load and whenever the cart drawer opens (not on every page).
  // ---------------------------------------------------------------------------
  function publishViewCart() {
    var c = readJSON(document.querySelector('script[data-ga4-cart]'));
    if (c) publish('wild_view_cart', c);
  }

  function initCart() {
    var main = document.querySelector('main[data-template]');
    var tmpl = main ? main.getAttribute('data-template') : '';
    if (/^cart(\.|$)/.test(tmpl)) publishViewCart();

    var dialog = document.querySelector('.cart-drawer__dialog');
    if (dialog && window.MutationObserver) {
      var mo = new MutationObserver(function () {
        if (dialog.hasAttribute('open')) publishViewCart();
      });
      mo.observe(dialog, { attributes: true, attributeFilter: ['open'] });
    }
  }

  // ---------------------------------------------------------------------------
  // remove_from_cart — the explicit "Remove" click on a cart line (drawer + cart
  // page). Shopify has no standard removal event, so publish it here. Resolve the
  // line's GA4 item by its row position within the SAME cart container, which
  // matches the [data-ga4-cart] items[] order (both loop cart.items in order).
  // Completes the cart funnel (add_to_cart → remove_from_cart → begin_checkout).
  // ---------------------------------------------------------------------------
  function initCartRemove() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.cart-items__remove');
      if (!btn) return;
      var row = btn.closest('.cart-items__table-row');
      if (!row) return;
      // Scope to this cart's container so the row index lines up with the right
      // [data-ga4-cart] (the drawer and the cart page each render their own).
      var container = btn.closest('.cart-drawer__dialog') || btn.closest('main') || document;
      var cart = readJSON(container.querySelector('script[data-ga4-cart]')) ||
                 readJSON(document.querySelector('script[data-ga4-cart]'));
      if (!cart || !cart.items) return;
      var rows = Array.prototype.slice.call(container.querySelectorAll('.cart-items__table-row'));
      var idx = rows.indexOf(row);
      var item = idx > -1 ? cart.items[idx] : null;
      if (!item) return;
      publish('wild_remove_from_cart', {
        currency: cart.currency,
        value: (item.price || 0) * (item.quantity || 1),
        items: [item]
      });
    });
  }

  // WP-F37: guide modals (install / measure / paper) open via [data-wp-page-modal].
  // The page-modal handles its triggers in the capture phase, so hook capture too.
  function initPageModal() {
    document.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-wp-page-modal]');
      if (!t) return;
      var guide = t.getAttribute('data-content-id') ||
                  t.getAttribute('data-page-url') ||
                  t.getAttribute('data-page-title') || 'guide';
      publish('wild_view_guide', { guide: guide });
    }, true);
  }

  // ---------------------------------------------------------------------------
  function init() {
    initSample();
    initWishlist();
    initVisualizer();
    initCustomizer();
    initQuickView();
    initSelectItem();
    initCart();
    initCartRemove();
    initPageModal();
    initForms();
    watchForms();
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  // Re-scan forms after AJAX section reloads (cart drawer, etc.).
  document.addEventListener('shopify:section:load', function () {
    initForms();
  });
})();
